// client/src/components/pages/Geofencing/zoneEvents.ts (ST-00269)
//
// Zone events on the Geofences page (Events tab) and the Pending Alerts / Events Today cards
// (Geofences and Live View): LexiStar's events about zones, since the legacy geofence evaluator
// was retired. The server decides which events are zone events and which are alerts
// (server/services/zoneActivityService); this module mirrors those two rules for the events
// that arrive live on the /tracking socket (`lexistar:event`), names the types, and words an
// event's detail. Pure: no React.
//
// ST-00282: what is an alert is decided ONCE, by the LexiStar engine, from the zone's Alerts
// switch and rules: its payload.alert. This module only reads that decision for a live event
// (the server reads the same field for the lists and counts); it never filters by the switch.
import { MINING_TZ } from '../../../utils/dateUtils';
import { eventTypeLabel } from '../../../utils/eventTypeLabels';
import type {
  GeofenceRow,
  LexiStarEventMessage,
  ZoneEvent,
  ZoneEventSeverity,
  ZoneEventShow,
} from '../../../types/clientPages/geofencing';

/** Zone-rule event types outside category GEOFENCE (a zone event only when it names a zone). */
export const ZONE_RULE_TYPES: readonly string[] = ['MOTION.OVERSPEED', 'ANALYTICS.DWELL_EXCEEDED'];
/** Crossings: an alert only when the engine marked it one (payload.alert). */
export const CROSSING_TYPES: readonly string[] = ['GEOFENCE.ENTERED', 'GEOFENCE.EXITED'];

export type ZoneEventTone = 'entry' | 'exit' | 'dwell' | 'danger' | 'warning' | 'info';

/** The event types the Events tab names, in its Type filter's order. */
export const ZONE_EVENT_TYPES: readonly { type: string; label: string; tone: ZoneEventTone }[] = [
  { type: 'GEOFENCE.ENTERED', label: 'Entered', tone: 'entry' },
  { type: 'GEOFENCE.EXITED', label: 'Exited', tone: 'exit' },
  { type: 'GEOFENCE.DWELL_EXCEEDED', label: 'Stayed too long', tone: 'dwell' },
  { type: 'GEOFENCE.DWELL_TOO_SHORT', label: 'Left too soon', tone: 'dwell' },
  { type: 'GEOFENCE.NOGO_ENTERED', label: 'Entered a no-go zone', tone: 'danger' },
  { type: 'GEOFENCE.AFTER_HOURS', label: 'Outside operating hours', tone: 'warning' },
  { type: 'MOTION.OVERSPEED', label: 'Speeding in the zone', tone: 'warning' },
  { type: 'ANALYTICS.DWELL_EXCEEDED', label: 'Site dwell over the limit', tone: 'dwell' },
];

export const SEVERITY_LABELS: Record<ZoneEventSeverity, string> = {
  I: 'Info',
  L: 'Low',
  M: 'Medium',
  H: 'High',
  C: 'Critical',
};

export const SHOW_OPTIONS: readonly { id: ZoneEventShow; label: string }[] = [
  { id: 'all', label: 'All zone events' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'open', label: 'Open alerts' },
];

export const isZoneEventShow = (v: unknown): v is ZoneEventShow =>
  v === 'all' || v === 'alerts' || v === 'open';

/** The zone-context words for a type; any other type in the shared catalogue's words (ST-00280). */
export function typeLabel(type: string): string {
  return ZONE_EVENT_TYPES.find((t) => t.type === type)?.label ?? eventTypeLabel(type);
}

export function typeTone(type: string): ZoneEventTone {
  return ZONE_EVENT_TYPES.find((t) => t.type === type)?.tone ?? 'info';
}

/** A live `lexistar:event` is a zone event (the server's rule). */
export function isZoneEventMessage(
  m: LexiStarEventMessage | null | undefined
): m is LexiStarEventMessage {
  if (!m || typeof m !== 'object' || typeof m.type !== 'string' || !m.id) return false;
  if (m.category === 'GEOFENCE') return true;
  return m.geofence_id != null && ZONE_RULE_TYPES.includes(m.type);
}

/**
 * A zone event is an alert (the server's rule, ST-00282): the engine's payload.alert where it is
 * present — a crossing always carries it, and every event of a zone whose Alerts switch is off
 * carries `false` — else any non-crossing zone event (a rule fired; recorded before ST-00282).
 */
export function isZoneAlert(
  type: string,
  payload: Record<string, unknown> | null | undefined
): boolean {
  if (payload && payload.alert !== undefined && payload.alert !== null) {
    return payload.alert === true || payload.alert === 'true';
  }
  return !CROSSING_TYPES.includes(type);
}

/** Counts as a Pending Alert: an alert nobody has acknowledged yet. */
export const isPendingAlert = (e: Pick<ZoneEvent, 'alert' | 'status'>): boolean =>
  e.alert && e.status === 'open';

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/** The plate in an asset key (reg:<plate>), else null. */
function plateOf(assetKey: string): string | null {
  return assetKey.startsWith('reg:') ? assetKey.slice(4) : null;
}

/** A live `lexistar:event` -> the Events tab row (zone name from the zone list when known). */
export function zoneEventFromLive(
  m: LexiStarEventMessage,
  zones: readonly GeofenceRow[]
): ZoneEvent {
  const payload = (m.payload && typeof m.payload === 'object' ? m.payload : {}) as Record<
    string,
    unknown
  >;
  const zone =
    m.geofence_id == null ? undefined : zones.find((z) => z.GeofenceID === m.geofence_id);
  const severity = (
    ['I', 'L', 'M', 'H', 'C'].includes(m.severity) ? m.severity : 'I'
  ) as ZoneEventSeverity;
  return {
    id: m.id,
    type: m.type,
    category: m.category,
    severity,
    status: m.status || 'open',
    eventTime: m.event_time,
    regNo: m.registration_normalised || plateOf(String(m.asset_key || '')),
    assetKey: String(m.asset_key || ''),
    geofenceId: m.geofence_id ?? null,
    geofenceName: zone?.Name || str(payload.geofence_name) || str(payload.site_name),
    purposeType: str(payload.purpose_type),
    alert: isZoneAlert(m.type, payload),
    dwellSeconds: num(payload.dwell_s) ?? num(payload.dwell_s_at_detection),
    thresholdSeconds: num(payload.threshold_s),
    maxSpeedKmh: num(payload.max_speed_kmh),
    limitKmh: num(payload.limit_kmh),
    direction: str(payload.direction),
    tripId: m.trip_id ?? null,
    lat: num(m.lat),
    lon: num(m.lon),
  };
}

const sastDayFormat = new Intl.DateTimeFormat('en-CA', {
  timeZone: MINING_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** The SAST calendar day (YYYY-MM-DD) of an instant. */
export const sastDayOf = (at: string | number | Date): string => sastDayFormat.format(new Date(at));

/** "2 h 5 min", "45 min", "under a minute". */
export function formatDuration(seconds: number): string {
  const totalMin = Math.round(seconds / 60);
  if (totalMin < 1) return 'under a minute';
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** The one line under an event: how long, how fast, which way. Empty when nothing to add. */
export function eventDetail(e: ZoneEvent): string {
  switch (e.type) {
    case 'GEOFENCE.EXITED':
      return e.dwellSeconds != null ? `${formatDuration(e.dwellSeconds)} in the zone` : '';
    case 'GEOFENCE.DWELL_EXCEEDED':
      return e.thresholdSeconds != null
        ? `in the zone for over ${formatDuration(e.thresholdSeconds)}`
        : '';
    case 'GEOFENCE.DWELL_TOO_SHORT':
      return e.dwellSeconds != null && e.thresholdSeconds != null
        ? `${formatDuration(e.dwellSeconds)} in the zone, less than ${formatDuration(e.thresholdSeconds)}`
        : '';
    case 'GEOFENCE.AFTER_HOURS':
      return e.direction === 'exit'
        ? 'left outside operating hours'
        : 'entered outside operating hours';
    case 'MOTION.OVERSPEED':
      return e.maxSpeedKmh != null && e.limitKmh != null
        ? `${Math.round(e.maxSpeedKmh)} km/h where the limit is ${Math.round(e.limitKmh)} km/h`
        : '';
    default:
      return '';
  }
}

/** Same rule as the server's registration filter (A-Z / 0-9, upper case). */
export const normaliseRegistration = (v: string | null | undefined): string =>
  String(v ?? '')
    .replace(/[^0-9A-Za-z]+/g, '')
    .toUpperCase();

/** The Events tab's filters, as the list request sends them. */
export interface ZoneEventFilters {
  /** SAST days, inclusive (YYYY-MM-DD). */
  from: string;
  to: string;
  type: string;
  geofenceId: string;
  regNo: string;
  show: ZoneEventShow;
}

/** A live event belongs in the list the filters asked for. */
export function matchesFilters(e: ZoneEvent, f: ZoneEventFilters): boolean {
  const day = sastDayOf(e.eventTime);
  if (day < f.from || day > f.to) return false;
  if (f.type && e.type !== f.type) return false;
  if (f.geofenceId && String(e.geofenceId) !== f.geofenceId) return false;
  if (f.regNo) {
    const want = normaliseRegistration(f.regNo);
    if (normaliseRegistration(e.regNo) !== want && e.assetKey !== `reg:${want}`) return false;
  }
  if (f.show === 'alerts' && !e.alert) return false;
  if (f.show === 'open' && !isPendingAlert(e)) return false;
  return true;
}
